import { useLayoutEffect } from "react";

export const MILESTONE_POPOVER_WIDTH = 248;
const EDGE_GAP = 6;
const VIEWPORT_PADDING = 12;
const PREFERRED_LIST_HEIGHT = 220;
const MIN_LIST_HEIGHT = 72;
const HEAD_CHROME_HEIGHT = 54;
const SEARCH_CHROME_HEIGHT = 42;

export function computeAnchoredPopoverPosition(
  triggerEl,
  { showSearch = false } = {},
) {
  const chromeHeight = HEAD_CHROME_HEIGHT + (showSearch ? SEARCH_CHROME_HEIGHT : 0);
  const width = MILESTONE_POPOVER_WIDTH;

  if (!triggerEl) {
    return {
      top: VIEWPORT_PADDING,
      bottom: null,
      left: VIEWPORT_PADDING,
      openUpward: false,
      listMaxHeight: PREFERRED_LIST_HEIGHT,
      width,
    };
  }

  const rect = triggerEl.getBoundingClientRect();

  let left = rect.left;
  if (left + width > window.innerWidth - VIEWPORT_PADDING) {
    left = rect.right - width;
  }
  left = Math.max(
    VIEWPORT_PADDING,
    Math.min(left, window.innerWidth - width - VIEWPORT_PADDING),
  );

  const spaceBelow = window.innerHeight - rect.bottom - EDGE_GAP;
  const spaceAbove = rect.top - EDGE_GAP;
  let openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;

  const usableSpace = openUpward ? spaceAbove : spaceBelow;
  let listMaxHeight = Math.max(
    MIN_LIST_HEIGHT,
    Math.min(PREFERRED_LIST_HEIGHT, usableSpace - chromeHeight - VIEWPORT_PADDING),
  );

  if (usableSpace - chromeHeight < MIN_LIST_HEIGHT) {
    openUpward = spaceAbove > spaceBelow;
    const nextUsableSpace = openUpward ? spaceAbove : spaceBelow;
    listMaxHeight = Math.max(
      MIN_LIST_HEIGHT,
      Math.min(PREFERRED_LIST_HEIGHT, nextUsableSpace - chromeHeight - VIEWPORT_PADDING),
    );
  }

  if (openUpward) {
    return {
      top: null,
      bottom: window.innerHeight - rect.top + EDGE_GAP,
      left,
      openUpward: true,
      listMaxHeight,
      width,
    };
  }

  return {
    top: rect.bottom + EDGE_GAP,
    bottom: null,
    left,
    openUpward: false,
    listMaxHeight,
    width,
  };
}

export function getAnchoredPopoverStyle(position, zIndex = 10060) {
  const base = {
    left: `${position.left}px`,
    width: `${position.width}px`,
    zIndex,
  };

  if (position.openUpward) {
    return { ...base, bottom: `${position.bottom}px`, top: "auto" };
  }

  return { ...base, top: `${position.top}px`, bottom: "auto" };
}

export function useAnchoredPopoverPosition(
  open,
  triggerRef,
  setPosition,
  options = {},
) {
  const { showSearch = false } = options;

  useLayoutEffect(() => {
    if (!open) return undefined;

    const syncPosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      setPosition(computeAnchoredPopoverPosition(trigger, { showSearch }));
    };

    syncPosition();
    const rafId = window.requestAnimationFrame(syncPosition);

    window.addEventListener("scroll", syncPosition, true);
    window.addEventListener("resize", syncPosition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", syncPosition, true);
      window.removeEventListener("resize", syncPosition);
    };
  }, [open, setPosition, showSearch, triggerRef]);
}
